/* Calzado Audor · ficha de producto — «¿Cuántos pares?»
   SOP-sistema-visual §9.27 · D-066

   Las fichas del carrito (B4), en la ficha de producto: el paso − 1 +, el titular, tres fichas
   con sus botas y la frase. Aquí la ficha SE TOCA: es un botón de radio, y tocarla pone esa
   cantidad. No hay confeti. Se carga como módulo.

   MANDA LA CANTIDAD, NO LA FICHA. La verdad es el `input[name="quantity"]` de Horizon, que no
   se mueve ni se duplica: se queda dentro del <form>, que es lo único que hace que la cantidad
   viaje al carrito. El paso, las fichas, el titular, la frase, el precio grande, la línea verde
   y el botón de pedir se pintan todos desde ese número, así que no pueden contradecirse.

   UN ESCALÓN ES «DESDE N PARES». Con 7 pares vale el de 5, que es lo que cobra Shopify. Antes,
   con una cantidad que no era escalón, la ficha se callaba y volvía al precio normal.

   DE DÓNDE SALE CADA CIFRA (SOP §9.27.4). Lo de cada escalón exacto llega YA ESCRITO EN PESOS
   desde Liquid, en `data-*`, y aquí solo se copia. Lo que Liquid no puede dejar escrito —lo
   que ahorran 7 pares, o el «{delta} más» de la frase— se calcula aquí con los centavos
   enteros que dejó Liquid (`data-*-c`) y se escribe con el formato de la tienda. Ese
   formateador SE PRUEBA A SÍ MISMO al conectar, contra cada cifra que Liquid ya escribió: si
   una sola no coincide letra por letra, no escribe ninguna cifra propia. Falla hacia decir
   menos, nunca hacia una cifra mal escrita.

   Manda el evento `aud:escalon`, que escuchan `aud-ficha.js` (precio y línea verde) y
   `aud-barra.js` (la barra fija). */

const EVENTO = 'aud:escalon';
const FICHAS = 3;
const POR_RENGLON = 5;
const SUAVE = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const VISTO =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3.2" stroke-linecap="round" ' +
  'stroke-linejoin="round" aria-hidden="true"><path d="M5 12.5l4.5 4.5L19 7.5"/></svg>';

/* Donde va la cifra dentro del titular, mientras se arma: un carácter que nadie teclea. */
const HUECO = '\u0001';

const seccionDe = (elemento) => elemento.closest('.shopify-section') || document;

const entero = (valor, respaldo) => {
  const n = parseInt(valor, 10);
  return Number.isFinite(n) ? n : respaldo;
};

function leerJson(texto) {
  try {
    return JSON.parse(texto || '{}') || {};
  } catch {
    return {};
  }
}

/* Rellena una plantilla. Si la plantilla pide un dato que no hay, devuelve vacío: una frase
   con un hueco en medio es peor que ninguna frase. */
function componer(plantilla, piezas) {
  if (!plantilla) return '';
  let falta = false;
  const texto = String(plantilla).replace(/\{(\w+)\}/g, (todo, clave) => {
    if (!(clave in piezas)) return todo;
    const valor = piezas[clave];
    if (valor === '' || valor === null || valor === undefined) falta = true;
    return String(valor ?? '');
  });
  return falta ? '' : texto;
}

/* ---------- dinero ---------- El mismo formateador del carrito (`aud-carrito.js`). */
function separar(centavos, mil, dec, conDecimales) {
  const negativo = centavos < 0;
  const v = Math.abs(Math.round(centavos));
  const parteEntera = String(Math.floor(v / 100)).replace(/\B(?=(\d{3})+(?!\d))/g, mil);
  const resto = String(v % 100).padStart(2, '0');
  return (negativo ? '-' : '') + parteEntera + (conDecimales ? dec + resto : '');
}

function pesos(formato, centavos) {
  return String(formato).replace(/\{\{\s*(\w+)\s*\}\}/g, (_, clave) => {
    switch (clave) {
      case 'amount': return separar(centavos, ',', '.', true);
      case 'amount_no_decimals': return separar(centavos, ',', '.', false);
      case 'amount_with_comma_separator': return separar(centavos, '.', ',', true);
      case 'amount_no_decimals_with_comma_separator': return separar(centavos, '.', ',', false);
      case 'amount_with_apostrophe_separator': return separar(centavos, "'", '.', true);
      case 'amount_with_space_separator': return separar(centavos, ' ', ',', true);
      case 'amount_no_decimals_with_space_separator': return separar(centavos, ' ', ',', false);
      default: return '';
    }
  });
}

/* Qué eligió el comprador, por ámbito (id de sección). Si la sección se repinta entera —en el
   editor pasa con cada ajuste—, el elemento nace de nuevo y sin esto la elección se perdería. */
const eleccionPorAmbito = new Map();

/* === <aud-escalones> === */
class AudEscalones extends HTMLElement {
  connectedCallback() {
    this.ambito = this.dataset.ambito || '';
    this.T = leerJson(this.querySelector('[data-aud-textos]')?.textContent);

    this.refFila = this.querySelector('[data-aud-fila]');
    this.refDice = this.querySelector('[data-aud-dice]');
    this.refDiceVe = this.querySelector('[data-aud-dice-ve]');
    this.refReserva = this.querySelector('[data-aud-reserva]');
    this.refFrase = this.querySelector('[data-aud-frase]');
    this.refPaso = this.querySelector('[data-aud-paso]');
    this.refValorPaso = this.querySelector('[data-aud-valor]');
    this.refMenos = this.querySelector('[data-aud-menos]');
    this.refMas = this.querySelector('[data-aud-mas]');

    /* Estas dos siguen en atributos y no en el JSON: `aud-barra.js` lee `anzueloPlantilla`
       de aquí mismo para su franja. Un texto, un sitio. */
    this.T.fraseAhorro = this.dataset.ahorroPlantilla || '';
    this.T.fraseAnzuelo = this.dataset.anzueloPlantilla || '';

    this.precioC = entero(this.dataset.precioC, 0);
    this.precioTxt = this.dataset.precio || '';
    this.formato = this.dataset.formato || '';

    this.esc = [...this.querySelectorAll('[data-aud-escalon]')]
      .map((boton) => this.leerEscalon(boton))
      .filter((e) => e.n > 1 && e.ahorroParC > 0)
      .sort((a, b) => a.n - b.n);

    this.fiable = this.probarFormato();
    this.n = null;
    this.desde = null;

    if (this.refPaso) seccionDe(this).classList?.add('aud-con-paso');

    this.addEventListener('click', this.alHacerClic);
    this.addEventListener('keydown', this.alTeclear);
    document.addEventListener('change', this.alMoverCantidad);

    this.prepararFichas();

    /* En microtarea: así <aud-precio>, que va antes en el DOM, ya está conectado. */
    queueMicrotask(() => {
      if (!this.isConnected) return;
      const recordada = eleccionPorAmbito.get(this.ambito);
      this.poner(recordada ?? this.cantidadDelCampo(), recordada !== undefined);
      this.reservar();
      this.vigilar();
    });
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.alHacerClic);
    this.removeEventListener('keydown', this.alTeclear);
    document.removeEventListener('change', this.alMoverCantidad);
    this.vigia?.disconnect();
    this.medidor?.disconnect();
  }

  /* ---------- leer lo que dejó Liquid ---------- */

  leerEscalon(boton) {
    const d = boton.dataset;
    return {
      boton,
      n: entero(d.cantidad, 0),
      porParC: entero(d.porParC, 0),
      totalC: entero(d.totalC, 0),
      ahorroParC: entero(d.ahorroParC, 0),
      ahorroTotalC: entero(d.ahorroTotalC, 0),
      porPar: d.porPar || '',
      total: d.total || '',
      ahorroPar: d.ahorroPar || '',
      ahorroTotal: d.ahorroTotal || '',
      botas: boton.querySelector('[data-aud-botas]'),
      estado: boton.querySelector('[data-aud-estado]'),
      rotulo: (boton.querySelector('[data-aud-pares]')?.textContent || '').trim(),
    };
  }

  /* EL FORMATEADOR SE PRUEBA CONTRA LIQUID. Cada cifra que Liquid escribió tiene al lado sus
     centavos: si al escribirlos aquí no sale LA MISMA cadena, el formato de la tienda es uno
     que este archivo no sabe escribir, y no se usa. */
  probarFormato() {
    if (!this.formato || !this.precioC || !this.precioTxt) return false;
    const pares = [[this.precioC, this.precioTxt]];
    for (const e of this.esc) {
      pares.push([e.porParC, e.porPar], [e.totalC, e.total], [e.ahorroParC, e.ahorroPar], [e.ahorroTotalC, e.ahorroTotal]);
    }
    return pares.every(([centavos, escrito]) => escrito !== '' && pesos(this.formato, centavos) === escrito);
  }

  dinero(centavos) {
    return this.fiable ? pesos(this.formato, centavos) : '';
  }

  /* ---------- la cantidad ---------- */

  campoReal() {
    return seccionDe(this).querySelector('input[name="quantity"]');
  }

  cantidadDelCampo() {
    const campo = this.campoReal();
    return entero(campo?.value, entero(campo?.getAttribute('min'), 1));
  }

  /* Mínimo, máximo e incremento salen del campo de Horizon, no de un número de aquí. El máximo
     es el efectivo: lo que deja el inventario menos lo que ya hay en el carrito, igual que en
     `component-quantity-selector.js`. */
  limites() {
    const campo = this.campoReal();
    const minimo = entero(campo?.getAttribute('min'), 1) || 1;
    const salto = entero(campo?.getAttribute('step'), 1) || 1;
    const maximo = entero(campo?.getAttribute('max'), null);
    const enCarrito = entero(campo?.getAttribute('data-cart-quantity'), 0);
    const tope = maximo === null ? null : Math.max(maximo - enCarrito, minimo);
    return { minimo, salto, tope };
  }

  acotar(valor) {
    const { minimo, salto, tope } = this.limites();
    let n = entero(valor, minimo);
    if ((n - minimo) % salto !== 0) n = minimo + Math.floor((n - minimo) / salto) * salto;
    if (tope !== null && n > tope) n = tope;
    if (n < minimo) n = minimo;
    return n;
  }

  /* El único sitio por donde cambia la cantidad. `escribir` dice si hay que llevarla al campo
     de Horizon o si ya viene de él. */
  poner(valor, escribir = true) {
    const n = this.acotar(valor);

    if (escribir) {
      const campo = this.campoReal();
      if (campo && campo.value !== String(n)) {
        this.aplicando = true;
        campo.value = String(n);
        campo.dispatchEvent(new Event('input', { bubbles: true }));
        campo.dispatchEvent(new Event('change', { bubbles: true }));
        this.aplicando = false;
      }
    }

    this.venia = this.n;
    this.n = n;
    eleccionPorAmbito.set(this.ambito, n);
    this.pintar();
  }

  /* ---------- la escalera ---------- */

  /* El escalón que vale para `n` pares: el más alto que no lo pase. */
  escalonDe(n) {
    let suyo = null;
    for (const e of this.esc) if (n >= e.n) suyo = e;
    return suyo;
  }

  /* Las cifras de `n` pares. En un escalón exacto son las de Liquid, copiadas. En una cantidad
     intermedia se calculan, y si el formateador no es fiable se devuelve `incierto`: no hay
     cifra que decir. */
  cifras(n) {
    const e = this.escalonDe(n);

    if (!e) {
      return {
        n,
        porPar: this.precioTxt,
        total: n === 1 ? this.precioTxt : this.dinero(this.precioC * n),
        ahorro: '',
        ahorroPar: '',
        ahorroC: 0,
        incierto: false,
      };
    }

    if (e.n === n) {
      return { n, porPar: e.porPar, total: e.total, ahorro: e.ahorroTotal, ahorroPar: e.ahorroPar, ahorroC: e.ahorroTotalC, incierto: false };
    }

    if (!this.fiable) {
      return { n, porPar: this.precioTxt, total: '', ahorro: '', ahorroPar: '', ahorroC: 0, incierto: true };
    }

    return {
      n,
      porPar: e.porPar,
      total: this.dinero(e.porParC * n),
      ahorro: this.dinero(e.ahorroParC * n),
      ahorroPar: e.ahorroPar,
      ahorroC: e.ahorroParC * n,
      incierto: false,
    };
  }

  /* LA VENTANA DE TRES: el último premio ganado y los que siguen. Es la del carrito. */
  ventana(n) {
    const cuantas = Math.min(FICHAS, this.esc.length);
    const sig = this.esc.findIndex((e) => e.n > n);
    const desde = sig === -1 ? this.esc.length - cuantas : Math.max(0, Math.min(sig - 1, this.esc.length - cuantas));
    return { desde, sig, cuantas };
  }

  /* «1 par», «2 pares». El número nunca va solo. */
  pares(k) {
    return k + ' ' + (k === 1 ? this.T.unidadUno || '' : this.T.unidadVarios || '');
  }

  /* ---------- pintar ---------- */

  pintar() {
    const n = this.n;
    const c = this.cifras(n);
    const v = this.ventana(n);
    const hay = this.esc.length > 0;

    this.pintarPaso();

    if (this.refFila) this.refFila.hidden = !hay;
    if (hay) this.pintarFichas(n, v);

    this.pintarTitular(n, c);
    this.pintarFrase(n, c, v);
    this.pintarBoton(n, c);
    this.avisar(n, c);
  }

  pintarPaso() {
    if (this.refValorPaso) this.refValorPaso.textContent = String(this.n);
    const { minimo, tope } = this.limites();
    if (this.refMenos) this.refMenos.disabled = this.n <= minimo;
    if (this.refMas) this.refMas.disabled = tope !== null && this.n >= tope;
  }

  /* Lo que no cambia con la cantidad: «ahorras» en cada ficha. */
  prepararFichas() {
    for (const e of this.esc) {
      const dice = e.boton.querySelector('[data-aud-ficha-dice]');
      if (dice) dice.textContent = this.T.fichaDice || '';
    }
  }

  pintarFichas(n, v) {
    const primera = this.desde === null;
    const paso = primera ? 0 : v.desde - this.desde;
    const venia = primera ? null : this.venia;
    /* LA ELEGIDA (D-067): el escalón que vale para `n` pares. Lleva el contorno, en verde. Con
       7 pares es el de 5, aunque ningún botón de radio quede marcado: ninguno dice «7». */
    const vale = this.escalonDe(n);
    let renglones = 0;
    let elegida = null;
    let suya = null;
    let siguiente = null;
    let primeraVista = null;

    this.esc.forEach((e, k) => {
      const dentro = k >= v.desde && k < v.desde + v.cuantas;
      const hecha = n >= e.n;
      const sigue = k === v.sig;
      const eraHecha = e.boton.classList.contains('is-hecha');
      const exacta = e.n === n;

      e.boton.classList.toggle('is-fuera', !dentro);
      e.boton.classList.toggle('is-hecha', hecha);
      e.boton.classList.toggle('is-suya', e === vale);
      e.boton.classList.toggle('is-sigue', sigue);
      /* Rebota la que se acaba de ganar, y solo esa. */
      e.boton.classList.toggle('is-recien', SUAVE && dentro && hecha && !eraHecha && venia !== null && venia < e.n);
      e.boton.setAttribute('aria-checked', exacta ? 'true' : 'false');
      e.boton.tabIndex = -1;
      if (dentro) {
        if (exacta) elegida = e;
        if (e === vale) suya = e;
        if (sigue) siguiente = e;
        if (!primeraVista) primeraVista = e;
      }

      const falta = e.n - n;
      const dice = hecha
        ? this.T.estadoHecho || ''
        : componer(falta === 1 ? this.T.estadoFaltaUno : this.T.estadoFaltan, { n: falta, pares: this.pares(falta) });
      const firma = (hecha ? 'v' : '') + dice;
      if (e.estado && e.estado.dataset.firma !== firma) {
        e.estado.textContent = '';
        if (hecha && dice) e.estado.insertAdjacentHTML('afterbegin', VISTO);
        if (dice) {
          const texto = document.createElement('span');
          texto.textContent = dice;
          e.estado.append(texto);
        }
        e.estado.dataset.firma = firma;
      }

      const puestas = Math.min(n, e.n);
      if (e.botas) {
        [...e.botas.children].forEach((bota, j) => {
          const puesta = j < puestas;
          const era = bota.classList.contains('is-puesta');
          bota.classList.toggle('is-puesta', puesta);
          bota.classList.toggle('is-recien', SUAVE && dentro && puesta && !era && venia !== null);
        });
        if (dentro) renglones = Math.max(renglones, Math.ceil(e.n / POR_RENGLON));
      }

      /* La ficha entera es un botón: lo que el ojo lee en cuatro renglones, el lector de
         pantalla lo oye en una frase. */
      const lectura = [
        e.rotulo + ', ' + (this.T.fichaDice || '') + ' ' + e.ahorroTotal,
        dice,
        componer(this.T.botasLectura, { n: puestas, max: e.n }),
      ].filter(Boolean).join('. ');
      if (e.boton.getAttribute('aria-label') !== lectura) e.boton.setAttribute('aria-label', lectura);
    });

    /* Con el teclado se entra por la ficha elegida; si no hay ninguna, por la que sigue. */
    const entrada = elegida || suya || siguiente || primeraVista;
    if (entrada) entrada.boton.tabIndex = 0;

    if (this.refFila) {
      this.refFila.style.setProperty('--aud-vol-renglones', String(renglones || 1));
      this.refFila.classList.add('is-encuadrada');
      this.refFila.classList.remove('is-corre');
      /* Si la ventana saltó más de un puesto —de 1 par a 10— no se anima: no hay recorrido. */
      if (SUAVE && Math.abs(paso) === 1) {
        this.refFila.style.setProperty('--aud-vol-corre', paso > 0 ? 'calc(100% + 8px)' : 'calc(-100% - 8px)');
        void this.refFila.offsetWidth;
        this.refFila.classList.add('is-corre');
      }
    }
    this.desde = v.desde;
  }

  /* El titular de `n` pares, como texto. La cifra va marcada con HUECO para poder ponerla
     después en negrita sin tocar el HTML con una cadena del editor. */
  textoTitular(n, c) {
    if (this.esc.length === 0 || c.incierto) return '';
    if (c.ahorroC > 0) {
      return c.ahorro ? componer(this.T.llevas, { pares: this.pares(n), n, ahorro: HUECO + c.ahorro + HUECO }) : '';
    }
    const primero = this.esc[0];
    return primero.n - n > 1
      ? componer(this.T.llevasLejos, { pares: this.pares(n), n: primero.n })
      : componer(this.T.llevasCero, { pares: this.pares(n), n });
  }

  pintarTitular(n, c) {
    if (!this.refDice || !this.refDiceVe) return;
    const texto = this.textoTitular(n, c);
    this.refDice.hidden = !texto && this.esc.length === 0;

    if (this.refDiceVe.dataset.firma === texto) return;
    this.refDiceVe.dataset.firma = texto;
    this.refDiceVe.textContent = '';
    texto.split(HUECO).forEach((trozo, i) => {
      if (!trozo) return;
      if (i % 2 === 1) {
        const cifra = document.createElement('b');
        cifra.textContent = trozo;
        this.refDiceVe.append(cifra);
      } else {
        this.refDiceVe.append(trozo);
      }
    });
  }

  /* LA RESERVA. De los titulares que pueden salir, se deja —invisible— el MÁS ALTO con la
     letra y el ancho de verdad. Se mide el nodo, no se adivina por el largo del texto. */
  reservar() {
    if (!this.refReserva || this.esc.length === 0) return;
    /* Todas las cantidades desde el mínimo hasta dos pares más allá del último escalón. No
       basta con los escalones: «Llevas 2 pares. Desde 5…» es dos letras más largo que
       «Llevas 1 par. Desde 5…», y con esas dos letras parte el renglón a 390 px (medido). */
    const candidatos = new Set();
    const hasta = Math.min((this.esc.at(-1)?.n || 0) + 2, 40);
    for (let n = this.limites().minimo; n <= hasta; n++) {
      const texto = this.textoTitular(n, this.cifras(n)).split(HUECO).join('');
      if (texto) candidatos.add(texto);
    }

    let mejor = '';
    let alto = -1;
    for (const texto of candidatos) {
      this.refReserva.textContent = texto;
      const h = this.refReserva.getBoundingClientRect().height;
      if (h > alto) {
        alto = h;
        mejor = texto;
      }
    }
    this.refReserva.textContent = mejor;
  }

  pintarFrase(n, c, v) {
    if (!this.refFrase) return;
    let dice = '';

    if (this.esc.length > 0 && !c.incierto) {
      if (v.sig === -1) {
        dice = componer(this.T.fraseTope, { n, porpar: this.escalonDe(n)?.ahorroPar || '' });
      } else {
        const e = this.esc[v.sig];
        const faltan = e.n - n;
        const datos = {
          n: e.n,
          faltan,
          ahorro: e.ahorroTotal,
          porpar: e.ahorroPar,
          delta: this.dinero(e.ahorroTotalC - c.ahorroC),
        };
        const plantilla = faltan > 1 ? this.T.fraseMayoreo : c.ahorroC > 0 ? this.T.fraseSuma : this.T.frasePrimera;
        dice = componer(plantilla, datos);
      }
    }

    if (this.refFrase.textContent !== dice) this.refFrase.textContent = dice;
    this.refFrase.hidden = !dice;
  }

  /* ---------- el botón de pedir ---------- */

  botonDePedir() {
    return [...seccionDe(this).querySelectorAll('add-to-cart-component button[name="add"]')].find(
      (b) => !b.closest('sticky-add-to-cart')
    );
  }

  /* «Pedir 2 pares · $260,000» dentro del botón de Horizon. Se cambia el nodo de texto, nunca
     el HTML. Y NO SE ESCRIBE SOBRE UN BOTÓN APAGADO: si la talla está agotada Horizon pone
     «Agotado», y ahí el texto es suyo. */
  pintarBoton(n = this.n, c = this.cifras(this.n)) {
    if (!this.T.botonUno && !this.T.botonVarios) return;

    const boton = this.botonDePedir();
    if (!boton) return;
    const nodo = [...boton.childNodes].find((h) => h.nodeType === Node.TEXT_NODE && h.nodeValue.trim() !== '');
    if (!nodo) return;

    /* Lo que hay escrito y no es nuestro, es de Horizon: se guarda para poder devolverlo. */
    if (nodo.nodeValue !== this.textoNuestro) this.textoNativo = nodo.nodeValue;
    if (boton.disabled) return;

    const texto = c.total ? componer(n === 1 ? this.T.botonUno : this.T.botonVarios, { n, total: c.total, porPar: c.porPar, ahorro: c.ahorro }) : '';
    const quiere = texto || this.textoNativo || '';
    if (!quiere || nodo.nodeValue === quiere) return;

    nodo.nodeValue = quiere;
    this.textoNuestro = texto ? quiere : undefined;
  }

  /* HORIZON REPINTA EL BOTÓN AL CAMBIAR DE TALLA (lección L-18 del SOP). `product-form.js` hace
     `morph()` sobre él en cada cambio de variante y se lleva nuestro texto; también puede
     acotar la cantidad sin avisar. Se vigila el formulario y se vuelve a poner todo en su sitio. */
  vigilar() {
    this.vigia?.disconnect();
    const raiz = seccionDe(this);
    const blanco = raiz.querySelector?.('product-form-component') || (raiz === document ? null : raiz);
    if (blanco && typeof MutationObserver === 'function') {
      this.vigia = new MutationObserver(() => this.avisado());
      this.vigia.observe(blanco, {
        childList: true,
        subtree: true,
        characterData: true,
        attributes: true,
        attributeFilter: ['disabled', 'min', 'max', 'step', 'data-cart-quantity'],
      });
    }

    /* La reserva del titular depende del ancho de la tarjeta y de que la letra ya haya llegado. */
    this.medidor?.disconnect();
    if (this.refDice && typeof ResizeObserver === 'function') {
      let ancho = this.refDice.getBoundingClientRect().width;
      this.medidor = new ResizeObserver(() => {
        const ahora = this.refDice.getBoundingClientRect().width;
        if (Math.abs(ahora - ancho) < 1) return;
        ancho = ahora;
        this.reservar();
      });
      this.medidor.observe(this.refDice);
    }
    document.fonts?.ready?.then(() => this.isConnected && this.reservar());
  }

  /* Muchas mutaciones, una sola respuesta. */
  avisado() {
    if (this.pendiente) return;
    this.pendiente = true;
    queueMicrotask(() => {
      this.pendiente = false;
      if (!this.isConnected) return;
      const delCampo = this.cantidadDelCampo();
      if (delCampo !== this.n) this.poner(delCampo, false);
      else {
        this.pintarPaso();
        this.pintarBoton();
      }
    });
  }

  /* ---------- reaccionar ---------- */

  alHacerClic = (evento) => {
    const menos = evento.target.closest('[data-aud-menos]');
    const mas = evento.target.closest('[data-aud-mas]');
    if (menos || mas) {
      evento.preventDefault();
      this.poner(this.n + (menos ? -1 : 1) * this.limites().salto);
      return;
    }
    const boton = evento.target.closest('[data-aud-escalon]');
    if (boton && this.contains(boton)) this.poner(entero(boton.dataset.cantidad, this.n));
  };

  /* Flechas: al escalón de al lado, sin dar la vuelta. El foco va a la ficha elegida, que para
     entonces ya está a la vista. */
  alTeclear = (evento) => {
    const adelante = evento.key === 'ArrowRight' || evento.key === 'ArrowDown';
    const atras = evento.key === 'ArrowLeft' || evento.key === 'ArrowUp';
    if (!adelante && !atras) return;
    if (!evento.target.closest('[data-aud-escalon]')) return;

    const suyo = this.esc.findIndex((e) => e.n === this.n);
    const debajo = this.esc.reduce((i, e, k) => (e.n <= this.n ? k : i), -1);
    let destino;
    if (adelante) destino = this.esc[Math.min(debajo + 1, this.esc.length - 1)];
    else destino = suyo > 0 ? this.esc[suyo - 1] : suyo === -1 && debajo >= 0 ? this.esc[debajo] : null;
    if (!destino) return;

    evento.preventDefault();
    this.poner(destino.n);
    if (this.n === destino.n) destino.boton.focus();
  };

  /* La cantidad cambió por otro lado: la barra fija, o el paso nativo. */
  alMoverCantidad = (evento) => {
    if (this.aplicando) return;
    const campo = evento.target;
    if (!(campo instanceof HTMLInputElement) || campo.name !== 'quantity') return;
    if (!seccionDe(this).contains(campo)) return;
    this.poner(campo.value, false);
  };

  avisar(n, c) {
    let lineaAhorro = '';
    let esAnzuelo = false;

    if (c.ahorroC > 0) {
      lineaAhorro = componer(this.T.fraseAhorro, { n, ahorro: c.ahorro, ahorroPar: c.ahorroPar, porPar: c.porPar, total: c.total });
    } else if (!c.incierto) {
      /* Sin ahorro todavía, se enseña el del primer escalón que lo da: la oferta existe desde
         que se abre la ficha. */
      const sig = this.esc.find((e) => e.n > n);
      if (sig) {
        esAnzuelo = true;
        lineaAhorro = componer(this.T.fraseAnzuelo, {
          n: sig.n,
          ahorro: sig.ahorroTotal,
          ahorroPar: sig.ahorroPar,
          porPar: sig.porPar,
          total: sig.total,
        });
      }
    }

    document.dispatchEvent(
      new CustomEvent(EVENTO, {
        detail: { ambito: this.ambito, cantidad: String(n), porPar: c.porPar, total: c.total, lineaAhorro, esAnzuelo },
      })
    );
  }
}

if (!customElements.get('aud-escalones')) customElements.define('aud-escalones', AudEscalones);
